import type { IEmittedDates, SgDaterangepickerCustomEvent, SupportedLanguage } from '@sg-bootstrap/components';
import { SgDaterangepicker, SgI18n } from '@sg-bootstrap/components/dist/react-library/src/components';
import { isDefined, isNotDefined } from '@sgme/fp';
import { formatISO, parseISO } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  BLOTTER_DATERANGE_CUSTOM_PRESETS_IDS,
  type BlotterFilterPresetRange,
  computeRangeForCustomPreset,
} from '@/store/blotter/daterangeCustomPresets.ts';
import { blotterSlice } from '@/store/blotter/slice.ts';
import { useAppSelector } from '@/store/hooks.ts';

const BLOTTER_DATERANGE_CUSTOM_PRESETS_IDS__JOINED = BLOTTER_DATERANGE_CUSTOM_PRESETS_IDS.join(',');

export const HistoricalDaterangePicker = () => {
  const dispatch = useDispatch();
  const { formatMessage, locale } = useIntl();

  const track = useTrackEvent();

  const filter = useAppSelector(blotterSlice.selectors.getFilter);

  const [dateRangeIsInvalid, setDateRangeIsInvalid] = useState(false);

  const dateRangePickerElement = useRef<HTMLSgDaterangepickerElement>(null);

  // these are the locale-dependent label displayed for the presets
  const presetsLabels = useMemo(
    () =>
      Object.fromEntries(
        BLOTTER_DATERANGE_CUSTOM_PRESETS_IDS.map((preset) => [
          preset,
          formatMessage({ id: `blotter.filters.date.preset.${preset}` }),
        ]),
      ),
    [formatMessage],
  );

  const handlePresetSelection = useCallback(
    async ({ detail: { preset } }: CustomEvent<{ preset: BlotterFilterPresetRange; element: HTMLElement }>) => {
      const { current: pickerApi } = dateRangePickerElement;
      if (!pickerApi) {
        return;
      }
      const range = computeRangeForCustomPreset(preset);
      await pickerApi.setStartDate(range[0]);
      await pickerApi.setEndDate(range[1]);
      await pickerApi.close();
      dispatch(blotterSlice.actions.filterModified({ type: 'historical', subtype: 'preset', preset }));
      track('Filter: daterange modified', `Historical - ${presetsLabels[preset]}`);
    },
    [presetsLabels],
  );

  const handleDaterangeChange = useCallback(({ detail: { from, to } }: SgDaterangepickerCustomEvent<IEmittedDates>) => {
    setDateRangeIsInvalid(isNotDefined(from) || isNotDefined(to));

    const fromDate = isDefined(from) ? formatISO(from.timestamp) : undefined;
    const toDate = isDefined(to) ? formatISO(to.timestamp) : undefined;

    dispatch(
      blotterSlice.actions.filterModified({
        type: 'historical',
        subtype: 'custom',
        range: {
          fromDate,
          toDate,
        },
      }),
    );

    track('Filter: daterange modified', `Historical - [${fromDate ?? '-∞'} - ${toDate ?? '+∞'}]`);
  }, []);

  if (filter === undefined) {
    // only case : failed to register to signalR notifications for trades
    return null;
  }

  const [initialStartDate, initialEndDate] =
    filter.type === 'historical'
      ? filter.subtype === 'preset'
        ? computeRangeForCustomPreset(filter.preset)
        : [
            filter.range.fromDate ? parseISO(filter.range.fromDate).getTime() : undefined,
            filter.range.toDate ? parseISO(filter.range.toDate).getTime() : undefined,
          ]
      : [];

  return (
    <>
      <SgI18n language={locale}>{JSON.stringify(presetsLabels)}</SgI18n>
      <SgDaterangepicker
        alignRight
        singleMonthCalendar
        size="sm"
        ref={dateRangePickerElement}
        language={locale as SupportedLanguage}
        startDate={initialStartDate}
        endDate={initialEndDate}
        customPresets={BLOTTER_DATERANGE_CUSTOM_PRESETS_IDS__JOINED}
        disablePresets="future,weekends"
        onCustomPresetClick={handlePresetSelection}
        onDateChanged={handleDaterangeChange}
        className={dateRangeIsInvalid ? 'border border-lg border-danger' : ''}
      />
    </>
  );
};
